<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useQuery, useQueryClient } from "@tanstack/vue-query";
import {
  UiButton,
  UiIcon,
  UiIconButton,
  UiInputField,
  UiNumberField,
  UiSelect,
  UiSwitch,
  UiText,
  useToaster,
} from "@shopexperts/shared";

import ConfidenceSignalLabelVariantsDialog from "./ConfidenceSignalLabelVariantsDialog.vue";
import {
  listConfidenceSignalGroups,
  updateConfidenceSignal,
  updateConfidenceSignalGroup,
  type ConfidenceSignalDefinition,
  type ConfidenceSignalGroup,
} from "@/app/services/confidenceSignalsApi.service";
import {
  getAdjacentSortSwap,
  isValidNFactor,
  parseNullableNFactor,
  type SortDirection,
} from "@/app/utils/confidenceSignalPresentation";

type DraftGroup = ConfidenceSignalGroup & { signals: ConfidenceSignalDefinition[] };

/** Parsed form of `signalSnapshot`, so the sort-only patch stays in step with it. */
type SignalSnapshot = Pick<
  ConfidenceSignalDefinition,
  "name" | "public_text" | "points" | "n_factor" | "scope" | "status" | "sort_index"
>;

const toaster = useToaster();
const queryClient = useQueryClient();
const search = ref("");
const variantsTarget = ref<ConfidenceSignalDefinition | null>(null);
const isVariantsDialogOpen = ref(false);
const draftGroups = ref<DraftGroup[]>([]);
const savedGroupSnapshots = ref(new Map<string, string>());
const savedSignalSnapshots = ref(new Map<string, string>());
const savingGroupKey = ref<string | null>(null);
const savingSignalKey = ref<string | null>(null);
const reorderingKeys = ref<string[]>([]);
const scopeOptions = [
  { label: "All experts", value: "all" },
  { label: "Agencies", value: "agency" },
  { label: "Freelancers", value: "freelancer" },
];

const { data: groupsResult, isLoading, error, refetch } = useQuery({
  queryKey: ["admin", "confidence-signal-groups"],
  queryFn: listConfidenceSignalGroups,
  staleTime: 60_000,
});

const groups = computed(() => groupsResult.value?.groups);

function cloneGroups(source: ConfidenceSignalGroup[]): DraftGroup[] {
  return source.map((group) => ({
    ...group,
    signals: (group.signals ?? []).map((signal) => ({ ...signal })),
  }));
}

/**
 * Keyed rather than positional so the sort-only patches below can target a
 * field by name instead of by index.
 */
function groupSnapshot(group: Pick<ConfidenceSignalGroup, "label" | "sort_index">) {
  return JSON.stringify({
    label: group.label.trim(),
    sort_index: Number(group.sort_index),
  });
}

function signalSnapshot(signal: ConfidenceSignalDefinition) {
  return JSON.stringify({
    name: signal.name.trim(),
    public_text: signal.public_text.trim(),
    points: Number(signal.points),
    n_factor: signal.n_factor,
    scope: signal.scope,
    status: signal.status,
    sort_index: Number(signal.sort_index),
  });
}

function captureSavedSnapshots(source: DraftGroup[]) {
  savedGroupSnapshots.value = new Map(
    source.map((group) => [group.key, groupSnapshot(group)]),
  );
  savedSignalSnapshots.value = new Map(
    source.flatMap((group) =>
      group.signals.map((signal) => [signal.key, signalSnapshot(signal)] as const),
    ),
  );
}

watch(
  groups,
  (next) => {
    if (!next) return;

    // Rows the admin has typed into but not saved yet. This query key is shared
    // with the presets panel, the page header, and the expert signals tab, so a
    // refetch can land mid-edit — it must not silently discard typed values.
    const dirtyLabels = new Map<string, string>();
    const dirtySignals = new Map<string, ConfidenceSignalDefinition>();
    for (const group of draftGroups.value) {
      if (isGroupDirty(group)) dirtyLabels.set(group.key, group.label);
      for (const signal of group.signals) {
        if (isSignalDirty(signal)) dirtySignals.set(signal.key, signal);
      }
    }

    const merged = cloneGroups(next);
    // Snapshots always describe what the server holds, so a preserved edit
    // stays dirty and its Save button stays enabled.
    captureSavedSnapshots(merged);

    for (const group of merged) {
      const label = dirtyLabels.get(group.key);
      if (label !== undefined) group.label = label;
      // Keep the admin's field edits, but take ordering from the server: sort
      // index is changed by the arrows, never by the form. Label variants are
      // server-owned too — the variants dialog refetches this query, and a
      // dirty row must not pin a stale variant list.
      group.signals = group.signals.map((signal) => {
        const dirty = dirtySignals.get(signal.key);
        return dirty
          ? { ...dirty, sort_index: signal.sort_index, label_variants: signal.label_variants }
          : signal;
      });
    }

    draftGroups.value = merged;
  },
  { immediate: true },
);

const orderedGroups = computed(() =>
  [...draftGroups.value].sort(
    (a, b) => a.sort_index - b.sort_index || a.key.localeCompare(b.key),
  ),
);

const filteredGroups = computed(() => {
  const query = search.value.trim().toLowerCase();
  return orderedGroups.value
    .map((group) => ({
      group,
      signals: [...group.signals]
        .sort((a, b) => a.sort_index - b.sort_index || a.key.localeCompare(b.key))
        .filter((signal) => {
          if (!query) return true;
          return [signal.key, signal.name, signal.public_text, signal.scope, group.label]
            .some((value) => value.toLowerCase().includes(query));
        }),
    }))
    .filter((group) => !query || group.signals.length > 0);
});

function isGroupDirty(group: DraftGroup) {
  return savedGroupSnapshots.value.get(group.key) !== groupSnapshot(group);
}

function isSignalDirty(signal: ConfidenceSignalDefinition) {
  return savedSignalSnapshots.value.get(signal.key) !== signalSnapshot(signal);
}

function isSignalEnabled(signal: ConfidenceSignalDefinition) {
  return signal.status === "enabled";
}

function setSignalEnabled(signal: ConfidenceSignalDefinition, enabled: boolean) {
  signal.status = enabled ? "enabled" : "disabled";
}

function setSignalPoints(signal: ConfidenceSignalDefinition, value: string | undefined) {
  signal.points = Number(value ?? 0);
}

function setSignalNFactor(signal: ConfidenceSignalDefinition, value: string | undefined) {
  signal.n_factor = parseNullableNFactor(value);
}

function setSignalScope(
  signal: ConfidenceSignalDefinition,
  value: string | string[] | undefined,
) {
  if (value === "all" || value === "agency" || value === "freelancer") {
    signal.scope = value;
  }
}

function groupPosition(group: DraftGroup) {
  return orderedGroups.value.findIndex((item) => item.key === group.key) + 1;
}

function signalPosition(group: DraftGroup, signal: ConfidenceSignalDefinition) {
  const ordered = [...group.signals].sort(
    (a, b) => a.sort_index - b.sort_index || a.key.localeCompare(b.key),
  );
  return ordered.findIndex((item) => item.key === signal.key) + 1;
}

function canMoveGroup(group: DraftGroup, direction: SortDirection) {
  return Boolean(getAdjacentSortSwap(draftGroups.value, group.key, direction));
}

function canMoveSignal(
  group: DraftGroup,
  signal: ConfidenceSignalDefinition,
  direction: SortDirection,
) {
  return Boolean(getAdjacentSortSwap(group.signals, signal.key, direction));
}

/**
 * Only the sort component is refreshed, deliberately: recomputing the whole
 * snapshot from the draft would mark a row the admin has typed into as clean
 * and silently drop the pending edit.
 */
function updateSavedGroupSort(groupKey: string, sortIndex: number) {
  const snapshot = savedGroupSnapshots.value.get(groupKey);
  if (!snapshot) return;
  const values = JSON.parse(snapshot) as { label: string; sort_index: number };
  values.sort_index = sortIndex;
  savedGroupSnapshots.value.set(groupKey, JSON.stringify(values));
}

function updateSavedSignalSort(signalKey: string, sortIndex: number) {
  const snapshot = savedSignalSnapshots.value.get(signalKey);
  if (!snapshot) return;
  const values = JSON.parse(snapshot) as SignalSnapshot;
  values.sort_index = sortIndex;
  savedSignalSnapshots.value.set(signalKey, JSON.stringify(values));
}

async function moveGroup(group: DraftGroup, direction: SortDirection) {
  const swap = getAdjacentSortSwap(draftGroups.value, group.key, direction);
  if (!swap || reorderingKeys.value.length > 0) return;

  const [current, adjacent] = swap;
  const currentSort = current.sort_index;
  const adjacentSort = adjacent.sort_index;
  reorderingKeys.value = [`group:${current.key}`, `group:${adjacent.key}`];
  current.sort_index = adjacentSort;
  adjacent.sort_index = currentSort;

  try {
    await Promise.all([
      updateConfidenceSignalGroup(current.key, { sort_index: current.sort_index }),
      updateConfidenceSignalGroup(adjacent.key, { sort_index: adjacent.sort_index }),
    ]);
    updateSavedGroupSort(current.key, current.sort_index);
    updateSavedGroupSort(adjacent.key, adjacent.sort_index);
    toaster.create({ title: "Group order updated", type: "success" });
  } catch (reorderError) {
    current.sort_index = currentSort;
    adjacent.sort_index = adjacentSort;
    console.error("[ConfidenceSignals] Failed to reorder groups", reorderError);
    // The two writes are independent: one may have landed. Reverting both
    // locally would then misrepresent the persisted order, so resync.
    await refetch();
  } finally {
    reorderingKeys.value = [];
  }
}

async function moveSignal(
  group: DraftGroup,
  signal: ConfidenceSignalDefinition,
  direction: SortDirection,
) {
  const swap = getAdjacentSortSwap(group.signals, signal.key, direction);
  if (!swap || reorderingKeys.value.length > 0) return;

  const [current, adjacent] = swap;
  const currentSort = current.sort_index;
  const adjacentSort = adjacent.sort_index;
  reorderingKeys.value = [`signal:${current.key}`, `signal:${adjacent.key}`];
  current.sort_index = adjacentSort;
  adjacent.sort_index = currentSort;

  try {
    await Promise.all([
      updateConfidenceSignal(current.key, { sort_index: current.sort_index }),
      updateConfidenceSignal(adjacent.key, { sort_index: adjacent.sort_index }),
    ]);
    updateSavedSignalSort(current.key, current.sort_index);
    updateSavedSignalSort(adjacent.key, adjacent.sort_index);
    toaster.create({ title: "Signal order updated", type: "success" });
  } catch (reorderError) {
    current.sort_index = currentSort;
    adjacent.sort_index = adjacentSort;
    console.error("[ConfidenceSignals] Failed to reorder signals", reorderError);
    // See moveGroup: a partial write must not leave a stale local order.
    await refetch();
  } finally {
    reorderingKeys.value = [];
  }
}

function variantCount(signal: ConfidenceSignalDefinition) {
  return signal.label_variants?.length ?? 0;
}

function openVariants(signal: ConfidenceSignalDefinition) {
  variantsTarget.value = signal;
  isVariantsDialogOpen.value = true;
}

/**
 * Variants are embedded in this query's definitions, so a create, reword, or
 * delete has to refresh it for the counts here to stay honest.
 */
async function refreshAfterVariantChange() {
  await queryClient.invalidateQueries({ queryKey: ["admin", "confidence-signal-groups"] });
}

async function saveGroup(group: DraftGroup) {
  if (!group.label.trim()) {
    toaster.create({ title: "Group label is required", type: "error" });
    return;
  }

  savingGroupKey.value = group.key;
  try {
    await updateConfidenceSignalGroup(group.key, { label: group.label.trim() });
    group.label = group.label.trim();
    savedGroupSnapshots.value.set(group.key, groupSnapshot(group));
    toaster.create({ title: `${group.label} group updated`, type: "success" });
  } catch (saveError) {
    console.error("[ConfidenceSignals] Failed to update group", saveError);
  } finally {
    savingGroupKey.value = null;
  }
}

async function saveSignal(signal: ConfidenceSignalDefinition) {
  if (!signal.name.trim() || !signal.public_text.trim()) {
    toaster.create({ title: "Name and public text are required", type: "error" });
    return;
  }

  if (!isValidNFactor(signal.n_factor)) {
    toaster.create({ title: "N factor must be zero or greater", type: "error" });
    return;
  }

  savingSignalKey.value = signal.key;
  try {
    await updateConfidenceSignal(signal.key, {
      name: signal.name.trim(),
      public_text: signal.public_text.trim(),
      points: Number(signal.points),
      n_factor: signal.n_factor,
      scope: signal.scope,
      status: signal.status,
    });
    signal.name = signal.name.trim();
    signal.public_text = signal.public_text.trim();
    savedSignalSnapshots.value.set(signal.key, signalSnapshot(signal));
    toaster.create({ title: `${signal.name} updated`, type: "success" });
  } catch (saveError) {
    console.error("[ConfidenceSignals] Failed to update signal", saveError);
  } finally {
    savingSignalKey.value = null;
  }
}
</script>

<template>
  <div class="flex flex-col gap-5">
    <div class="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <UiText as="p" variant="p-small" class="max-w-2xl text-se-gray-600">
        Keys and calculation sources are code-managed. Everything below — public language, scoring,
        thresholds, visibility, and display order — is editable here.
      </UiText>
      <div class="w-full sm:w-[22rem]">
        <label for="confidence-signal-search" class="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">
          Find a definition
        </label>
        <UiInputField
          v-model="search"
          icon="search"
          name="confidence-signal-search"
          placeholder="Search name, copy, or key"
        />
      </div>
    </div>

    <div v-if="isLoading" class="rounded-lg border border-gray-100 bg-white py-20 text-center">
      <div class="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-2 border-gray-200 border-t-emerald-600" />
      <UiText as="p" variant="p-small" class="text-gray-500">Loading trust signals...</UiText>
    </div>

    <div v-else-if="error" class="rounded-lg border border-red-200 bg-red-50 p-6 text-center">
      <UiText as="p" variant="p" class="font-medium text-red-700">Trust signals could not be loaded.</UiText>
      <UiButton class="mt-4" variant="outline" label="Try again" @click="refetch()" />
    </div>

    <div v-else-if="filteredGroups.length === 0" class="rounded-lg border border-dashed border-gray-300 bg-white py-16 text-center">
      <UiIcon name="search" class="mx-auto mb-3 h-7 w-7 text-gray-300" />
      <UiText as="p" variant="p" class="text-gray-500">No trust signals match your search.</UiText>
    </div>

    <section
      v-for="groupView in filteredGroups"
      v-else
      :key="groupView.group.key"
      class="overflow-hidden rounded-lg border border-gray-200 bg-white"
    >
      <div class="grid gap-3 border-b border-gray-200 bg-gray-50/80 px-4 py-3 lg:grid-cols-[minmax(12rem,1fr)_minmax(16rem,28rem)_auto_auto] lg:items-center">
        <div class="flex min-w-0 items-center gap-3">
          <span class="flex h-8 min-w-8 items-center justify-center rounded bg-gray-900 px-2 font-mono text-[11px] text-white">
            {{ String(groupPosition(groupView.group)).padStart(2, '0') }}
          </span>
          <div class="min-w-0">
            <p class="truncate font-mono text-xs text-gray-600">{{ groupView.group.key }}</p>
            <p class="mt-0.5 text-[11px] text-gray-400">{{ groupView.group.signals.length }} definitions</p>
          </div>
        </div>

        <div>
          <label class="sr-only" :for="`group-${groupView.group.key}-label`">Group label</label>
          <UiInputField
            v-model="groupView.group.label"
            :name="`group-${groupView.group.key}-label`"
            placeholder="Public group label"
          />
        </div>

        <div class="flex items-center gap-1">
          <UiIconButton
            icon="chevron-down"
            variant="outline"
            class="h-9 w-9 rotate-180"
            :aria-label="`Move ${groupView.group.label} group up`"
            :disabled="!canMoveGroup(groupView.group, 'up') || reorderingKeys.length > 0"
            @click="moveGroup(groupView.group, 'up')"
          />
          <UiIconButton
            icon="chevron-down"
            variant="outline"
            class="h-9 w-9"
            :aria-label="`Move ${groupView.group.label} group down`"
            :disabled="!canMoveGroup(groupView.group, 'down') || reorderingKeys.length > 0"
            @click="moveGroup(groupView.group, 'down')"
          />
        </div>

        <UiButton
          variant="outline"
          :label="savingGroupKey === groupView.group.key ? 'Saving...' : 'Save group'"
          :disabled="!isGroupDirty(groupView.group) || savingGroupKey === groupView.group.key || reorderingKeys.length > 0"
          @click="saveGroup(groupView.group)"
        />
      </div>

      <div class="hidden grid-cols-[minmax(10rem,0.8fr)_minmax(13rem,1fr)_minmax(16rem,1.3fr)_6rem_6rem_8rem_5rem_6.5rem] gap-3 border-b border-gray-100 bg-white px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.1em] text-gray-400 lg:grid">
        <span>Status / key</span>
        <span>Internal name</span>
        <span>Public text</span>
        <span>Points</span>
        <span>N factor</span>
        <span>Scope</span>
        <span class="text-center">Order</span>
        <span class="text-right">Action</span>
      </div>

      <div class="divide-y divide-gray-100">
        <article
          v-for="signal in groupView.signals"
          :key="signal.key"
          class="grid gap-3 px-4 py-3 transition-colors lg:grid-cols-[minmax(10rem,0.8fr)_minmax(13rem,1fr)_minmax(16rem,1.3fr)_6rem_6rem_8rem_5rem_6.5rem] lg:items-center"
          :class="signal.status === 'disabled' ? 'bg-gray-50/70' : 'bg-white hover:bg-gray-50/40'"
        >
          <div class="flex min-w-0 items-center gap-2.5">
            <UiSwitch
              :model-value="isSignalEnabled(signal)"
              size="sm"
              :name="`${signal.key}-enabled`"
              @update:model-value="setSignalEnabled(signal, $event)"
            />
            <span class="truncate font-mono text-[11px] text-gray-600" :title="signal.key">{{ signal.key }}</span>
          </div>

          <div>
            <label :for="`${signal.key}-name`" class="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-gray-400 lg:hidden">Internal name</label>
            <UiInputField v-model="signal.name" :name="`${signal.key}-name`" />
          </div>

          <div>
            <label :for="`${signal.key}-public-text`" class="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-gray-400 lg:hidden">Public text</label>
            <!-- The variants control sits beside the field, not under it: an
                 extra line in this cell would push the input off the centre
                 line every other input in the row sits on. -->
            <div class="flex items-center gap-2">
              <div class="min-w-0 flex-1">
                <UiInputField v-model="signal.public_text" :name="`${signal.key}-public-text`" />
              </div>
              <button
                type="button"
                class="flex h-10 shrink-0 items-center gap-1 rounded-md border border-gray-200 px-2 text-[11px] font-medium text-gray-500 transition-colors hover:border-gray-400 hover:text-gray-800"
                :title="variantCount(signal) > 0
                  ? `${variantCount(signal)} label variant${variantCount(signal) === 1 ? '' : 's'} for ${signal.name}`
                  : `Add a label variant for ${signal.name}`"
                :aria-label="`Manage label variants for ${signal.name}`"
                @click="openVariants(signal)"
              >
                <UiIcon name="pencil" class="h-3.5 w-3.5" />
                <span class="tabular-nums">{{ variantCount(signal) || '+' }}</span>
              </button>
            </div>
          </div>

          <div>
            <label :for="`${signal.key}-points`" class="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-gray-400 lg:hidden">Points</label>
            <UiNumberField
              :model-value="String(signal.points)"
              :name="`${signal.key}-points`"
              :min="0"
              @update:model-value="setSignalPoints(signal, $event)"
            />
          </div>

          <div>
            <label :for="`${signal.key}-n-factor`" class="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-gray-400 lg:hidden">N factor</label>
            <UiNumberField
              :model-value="signal.n_factor === null ? '' : String(signal.n_factor)"
              :name="`${signal.key}-n-factor`"
              :min="0"
              :step="0.1"
              placeholder="—"
              @update:model-value="setSignalNFactor(signal, $event)"
            />
          </div>

          <div>
            <label :for="`${signal.key}-scope`" class="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-gray-400 lg:hidden">Scope</label>
            <UiSelect
              :model-value="signal.scope"
              :name="`${signal.key}-scope`"
              :options="scopeOptions"
              @update:model-value="setSignalScope(signal, $event)"
            />
          </div>

          <div class="flex items-center gap-1 lg:justify-center">
            <span class="mr-1 font-mono text-[10px] text-gray-400 lg:hidden">
              {{ signalPosition(groupView.group, signal) }}
            </span>
            <UiIconButton
              icon="chevron-down"
              variant="ghost"
              class="h-8 w-8 rotate-180 border border-gray-200"
              :aria-label="`Move ${signal.name} up`"
              :disabled="!canMoveSignal(groupView.group, signal, 'up') || reorderingKeys.length > 0"
              @click="moveSignal(groupView.group, signal, 'up')"
            />
            <UiIconButton
              icon="chevron-down"
              variant="ghost"
              class="h-8 w-8 border border-gray-200"
              :aria-label="`Move ${signal.name} down`"
              :disabled="!canMoveSignal(groupView.group, signal, 'down') || reorderingKeys.length > 0"
              @click="moveSignal(groupView.group, signal, 'down')"
            />
          </div>

          <UiButton
            variant="primary"
            class="w-full"
            :label="savingSignalKey === signal.key ? 'Saving...' : 'Save'"
            :disabled="!isSignalDirty(signal) || savingSignalKey === signal.key || reorderingKeys.length > 0"
            @click="saveSignal(signal)"
          />
        </article>
      </div>
    </section>

    <ConfidenceSignalLabelVariantsDialog
      v-model="isVariantsDialogOpen"
      :signal="variantsTarget"
      @changed="refreshAfterVariantChange"
    />
  </div>
</template>
